/* eslint-disable no-unused-vars */
/* eslint-disable no-undef */
import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';

import LoadingIndicator from 'v-c/LoadingIndicator';

import locationQuery from '../../../global/lib/locationQuery';
import { showFlash } from '../../../global/state_managers/FlashMessagesStateManager';
import sanitizeAndProtectRedirectUrl from './sanitizeAndProtectRedirectUrl';
import navigateToRedirectPath from './navigateToRedirectPath';

import { LOGIN_PROVIDERS } from '../../../../../../config/app.json';

import PAGES from '../../../global/constants/Pages';

const VALID_FLASH_VIEWS = [PAGES.LOGIN];

const { OPENID } = LOGIN_PROVIDERS;

// The SSO token/hash in the login URL is a ONE-TIME credential: the backend
// consumes it on the first create_from_openid_token / token-login call and
// rejects (500) any replay. React can construct/mount this component more than
// once for the same URL (StrictMode double-invoke, or a remount triggered when
// Login's clearAppState() resets the store), which otherwise fired the exchange
// twice -- the second call reused the spent hash, 500'd, and surfaced as
// "Login failure. Try again." even though the first call had logged the user in.
// Track which credentials have been attempted (module-level, survives remounts)
// so each is only ever exchanged once (SNAR-9852).
const attemptedCredentials = new Set<string>();

// Test hook: clears the one-time-credential guard so specs can re-use the same
// token/hash value across cases. Not used in production.
export function resetAttemptedCredentials() {
  attemptedCredentials.clear();
}

type Props = {
  location: {
    query: boolean
  }
  getMessage: Function
  authentication: {
    redirectTo: string
    loggedInAs: {
      authenticationToken: string
    }
    loginSuccessful: boolean
    lastAttemptFailed: boolean
  }
  showFlash: Function
  startAttemptingLogin: Function
  startAttemptingTokenLogin: Function
  startAttemptingOpenIDLogin: Function
  clearLoggedInAs: Function
}

function getLoadingIndicator(getMessage: Function) {
  return (
    <LoadingIndicator
      orientation="middle"
      text={getMessage('app.general.loading')}
      isLoading
    />
  );
}

export default function loginAndRedirectHandler({
  goToHomePage,
  getAccountDetails,
}: any) {
  return (component: any) => {
    class LoginAndRedirectHandler extends Component<Props> {
      static propTypes = {
        location: PropTypes.object.isRequired,
        getMessage: PropTypes.func.isRequired,
        authentication: PropTypes.object,
        showFlash: PropTypes.func.isRequired,
        startAttemptingLogin: PropTypes.func.isRequired,
        startAttemptingTokenLogin: PropTypes.func.isRequired,
        startAttemptingOpenIDLogin: PropTypes.func.isRequired,
        clearLoggedInAs: PropTypes.func.isRequired,
      };

      static defaultProps = {
        authentication: {},
      };

      static getDerivedStateFromProps(nextProps: Props, prevState: any) {
        if (!prevState.showForm && nextProps.authentication.lastAttemptFailed) {
          nextProps.showFlash('login.other_failure.failure', VALID_FLASH_VIEWS);

          return { showForm: true };
        }

        return null;
      }

      constructor(props: any) {
        super(props);

        const { token, hash } = this.query;

        // Hide the login form when we're about to exchange an SSO credential, so
        // the user sees the loading state instead of a flash of the form. The
        // actual exchange is dispatched in componentDidMount (a constructor is
        // not a safe place for side effects -- it can run more than once).
        this.state = { showForm: !(token || hash) };
      }

      componentDidMount() {
        const { token, hash } = this.query;
        const openIDError = this.query[OPENID.ERROR_PARAM];

        // alert us of errors that may sneak in here
        try {
          // The token/hash is a one-time credential; only exchange each once,
          // even across remounts or StrictMode double-invokes (SNAR-9852).
          if (token && !attemptedCredentials.has(token)) {
            attemptedCredentials.add(token);
            this.props.clearLoggedInAs();

            this.props.startAttemptingTokenLogin({
              token,
              redirectTo: this.redirectTo,
            });
          }

          if (hash && !attemptedCredentials.has(hash)) {
            attemptedCredentials.add(hash);
            this.props.clearLoggedInAs();

            this.props.startAttemptingOpenIDLogin({
              hash,
              redirectTo: this.redirectTo,
            });
          }
        } catch (e) {
          console.error(e);
        }

        if (openIDError) {
          this.props.showFlash(
            `openid.errors.${openIDError}.failure`,
            VALID_FLASH_VIEWS,
          );
        }
      }

      componentDidUpdate(prevProps: any) {
        if (!this.props.authentication) {
          throw new Error('Must pass in authentication store');
        }

        if (
          !prevProps.authentication.loginSuccessful &&
          this.props.authentication.loginSuccessful
        ) {
          const {
            redirectTo,
            loggedInAs: { authenticationToken, roles },
          } = this.props.authentication;

          if (redirectTo) {
            navigateToRedirectPath(
              getAccountDetails().siteName,
              authenticationToken,
              redirectTo,
            );
          } else {
            // Pass the just-authenticated roles so the homepage lookup doesn't
            // read the userRights store, which the Login mount's clearAppState()
            // can momentarily reset to [] on the SSO path (SNAR-9852).
            goToHomePage(roles);
          }
        }
      }

      get query() {
        // Prefer an explicitly injected `location` (e.g. the loader-built one or
        // a test prop). After the react-router v8 migration the Login route no
        // longer receives `location` at all, so fall back to parsing the live
        // query string. Without this fallback the SSO `hash`/`token` handed back
        // on the login redirect was never read: startAttemptingOpenIDLogin never
        // fired, SSO looped back to /login, and the app hung on "Domain info
        // loading..." (SNAR-9852).
        if (this.props.location && this.props.location.query) {
          return this.props.location.query;
        }

        return locationQuery(window.location);
      }

      get redirectTo() {
        return this.query.redirect_to;
      }

      performLoginWithRedirect = (data: any) => {
        sanitizeAndProtectRedirectUrl(
          this.redirectTo,
          (url: string) => {
            this.props.startAttemptingLogin({
              ...data,
              redirectTo: url,
            });
          },
          () => {
            this.props.startAttemptingLogin(data);
          },
        );
      };

      render() {
        if (!this.state.showForm) {
          return getLoadingIndicator(this.props.getMessage);
        }

        return React.createElement(component, {
          ...this.props,
          performLoginWithRedirect: this.performLoginWithRedirect,
        });
      }
    }

    return connect(
      null,
      { showFlash },
    )(LoginAndRedirectHandler);
  };
}
